import {
  Grid,
  Stack,
  Slide,
  Dialog,
  Button,
  useTheme,
  TextField,
  Typography,
  IconButton,
  DialogTitle,
  DialogContent,
  useMediaQuery,
  DialogActions,
  CircularProgress,
} from '@mui/material';

import { Mapir } from 'components';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { useState, useEffect } from 'react';
import { useMutation } from '@apollo/client';

import graph from './graph';
import validate from 'validate.js';
import SearchAddress from './SearchAddress';
import CloseRounded from '@mui/icons-material/CloseRounded';

const schema = {
  title: {
    presence: { allowEmpty: false, message: 'وارد کردن این فیلد الزامی است' },
    length: { maximum: 64 },
  },
  tellphone: {
    presence: { allowEmpty: false, message: 'وارد کردن این فیلد الزامی است' },
    length: { maximum: 11 },
  },
  main_street: {
    length: { maximum: 128 },
  },
  minor_address: {
    length: { maximum: 128 },
  },
  postal_code: {
    length: { maximum: 10 },
  },
};

export default function AddressCreationOrUpdate({ address, refetch }) {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const { userToken } = useSelector((state) => state.auth);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [location, setLocation] = useState([address?.lng || '51.721715', address?.lat || '32.696533']);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const [createAddress, { loading }] = useMutation(graph.create.query, {
    context: {
      serviceName: graph.create.serviceName,
      headers: {
        authorization: `Bearer ${userToken}`,
      },
    },
  });

  const [updateAddress, { loading: updating }] = useMutation(graph.update.query, {
    context: {
      serviceName: graph.update.serviceName,
      headers: {
        authorization: `Bearer ${userToken}`,
      },
    },
  });

  const [formState, setFormState] = useState({
    isValid: false,
    values: {
      ids: address?.id || undefined,
      title: address?.title || undefined,
      tellphone: address?.tellphone || undefined,
      main_street: address?.main_street || undefined,
      postal_code: address?.postal_code || undefined,
      minor_address: address?.minor_address || undefined,
    },
    touched: {},
    errors: {},
  });

  const handleChange = (event) => {
    event.persist();
    setFormState((formState) => ({
      ...formState,
      values: {
        ...formState.values,
        [event.target.name]: event.target.type === 'checkbox' ? event.target.checked : event.target.value,
      },
      touched: {
        ...formState.touched,
        [event.target.name]: true,
      },
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    try {
      if (address) {
        const { data } = await updateAddress({
          variables: formState.values,
        });
        if (data) {
          toast.success(String(data[graph.update.name].messages[0]));
        }
      } else {
        const { data } = await createAddress({
          variables: formState.values,
        });
        if (data) {
          toast.success(String(data[graph.create.name].messages[0]));
        }
      }
      refetch && refetch();
      handleClose();
    } catch (error) {}
  };

  const setAddress = ({ title, compact, primary, postal }) => {
    setFormState((prevState) => ({
      ...prevState,
      values: {
        ...prevState.values,
        main_street: compact,
        minor_address: primary,
        postal_code: postal,
      },
    }));
  };

  useEffect(() => {
    const errors = validate(formState.values, schema);
    setFormState((formState) => ({
      ...formState,
      isValid: errors ? false : true,
      errors: errors || {},
    }));
  }, [formState.values]);

  const hasError = (field) => (formState.touched[field] && formState.errors[field] ? true : false);

  return (
    <>
      {address ? (
        <Button variant="contained" size="large" color="info" onClick={handleOpen}>
          ویرایش
        </Button>
      ) : (
        <Button variant="contained" size="large" color="error" onClick={handleOpen}>
          ثبت آدرس جدید
        </Button>
      )}
      <Dialog
        dir="rtl"
        fullWidth
        maxWidth="sm"
        open={open}
        scroll="paper"
        onClose={handleClose}
        fullScreen={isMobile}
        aria-labelledby="address-creation-dialog-title"
      >
        <DialogTitle id="address-creation-dialog-title" sx={{ p: 2 }}>
          <Stack direction="row" alignItems="center" columnGap={0.5}>
            <Typography flexGrow={1} variant="subtitle1" color="text.primary">
              آدرس جدید
            </Typography>
            <IconButton
              size="small"
              color="error"
              aria-label="close"
              onClick={handleClose}
              sx={{ bgcolor: 'action.hover' }}
            >
              <CloseRounded fontSize="small" />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent
          sx={{
            p: 2,
            rowGap: 0.5,
            minHeight: 200,
            display: 'flex',
            pb: { xs: 15, sm: 2 },
            flexDirection: 'column',
            justifyContent: 'flex-start',
          }}
        >
          <Stack rowGap={1} justifyContent="center">
            <SearchAddress changeCenter={setLocation} />
            <Mapir height="30vh" setAddress={setAddress} setLocation={setLocation} center={location} />
            <Grid container>
              <Grid item xs={6} px={0.5}>
                <TextField
                  error={hasError('title')}
                  fullWidth
                  helperText={hasError('title') ? formState.errors.title[0] : null}
                  label="عنوان آدرس"
                  name="title"
                  onChange={handleChange}
                  type="text"
                  value={formState.values.title || ''}
                  variant="outlined"
                  margin="dense"
                  size="medium"
                />
              </Grid>
              <Grid item xs={6} px={0.5}>
                <TextField
                  error={hasError('tellphone')}
                  fullWidth
                  helperText={hasError('tellphone') ? formState.errors.tellphone[0] : null}
                  label="تلفن همراه تحویل گیرنده"
                  name="tellphone"
                  onChange={handleChange}
                  type="tel"
                  value={formState.values.tellphone || ''}
                  variant="outlined"
                  margin="dense"
                  size="medium"
                />
              </Grid>
              <Grid item xs={12} px={0.5}>
                <TextField
                  error={hasError('main_street')}
                  fullWidth
                  helperText={hasError('main_street') ? formState.errors.main_street[0] : null}
                  label="خیابان اصلی"
                  name="main_street"
                  onChange={handleChange}
                  type="text"
                  value={formState.values.main_street || ''}
                  variant="outlined"
                  margin="dense"
                  size="small"
                  multiline
                  rows={2}
                />
              </Grid>
              <Grid item xs={12} px={0.5}>
                <TextField
                  error={hasError('minor_address')}
                  fullWidth
                  helperText={hasError('minor_address') ? formState.errors.minor_address[0] : null}
                  label="جزئیات آدرس"
                  name="minor_address"
                  onChange={handleChange}
                  type="text"
                  value={formState.values.minor_address || ''}
                  variant="outlined"
                  margin="dense"
                  size="small"
                  multiline
                  rows={2}
                />
              </Grid>
            </Grid>
          </Stack>
        </DialogContent>
        <DialogActions
          sx={{
            p: 2,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 10,
            rowGap: 0.5,
            display: 'flex',
            alignItems: 'stretch',
            justifyContent: 'center',
            flexDirection: 'column',
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            height: { xs: 80, sm: 'auto' },
            position: { xs: 'fixed', sm: 'unset' },
            bgcolor: { xs: '#fff', sm: 'transparent' },
            boxShadow: { xs: '0 -8px 16px -8px #00000022', sm: 'none' },
          }}
        >
          <Slide in unmountOnExit direction="up">
            <Stack direction="row" columnGap={1}>
              <Button
                fullWidth
                size="large"
                color="primary"
                variant="contained"
                onClick={handleSubmit}
                sx={{
                  borderRadius: 2,
                }}
                disabled={!formState.isValid || loading || updating}
              >
                {loading || updating ? <CircularProgress color="inherit" size={24} /> : address ? 'ویرایش' : 'ثبت آدرس'}
              </Button>
            </Stack>
          </Slide>
        </DialogActions>
      </Dialog>
    </>
  );
}
